import React, { useState } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

const AboutFederalCourts = () => {
  const [activeTab, setActiveTab] = useState('structure');

  const tabs = [
    { key: 'structure', label: 'Court Structure' },
    { key: 'judges', label: 'Federal Judges' },
    { key: 'jury', label: 'Jury Service' },
    { key: 'defender', label: 'Defender Services' },
  ];

  const courtTypes = [
    {
      title: 'Supreme Court',
      description:
        'The highest court in the federal system. The Supreme Court has ultimate appellate jurisdiction over all federal and state courts.',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2L2 7v2h20V7L12 2zM4 11v8h2v-8H4zm4 0v8h2v-8H8zm4 0v8h2v-8h-2zm4 0v8h2v-8h-2zm4 0v8h2v-8h-2zM2 21h20v2H2v-2z"/>
        </svg>
      ),
    },
    {
      title: 'Courts of Appeals',
      description:
        'Thirteen appellate courts that sit below the U.S. Supreme Court and above the district courts. They review decisions from the lower courts.',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M3 3h18v2H3zm0 4h18v2H3zm0 4h12v2H3zm0 4h12v2H3zm0 4h18v2H3z"/>
        </svg>
      ),
    },
    {
      title: 'District Courts',
      description:
        'The general trial courts of the federal system. Both civil and criminal cases are filed in district courts, which are located in 94 districts.',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 3L1 9l4 2.18V17h2v-4.82l2 1.09V17c0 2.21 3.58 4 8 4s8-1.79 8-4v-3.73L23 9 12 3z"/>
        </svg>
      ),
    },
    {
      title: 'Bankruptcy Courts',
      description:
        'Each judicial district has a bankruptcy court as a unit of the district court. Federal courts have exclusive jurisdiction over bankruptcy cases.',
      icon: (
        <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/>
        </svg>
      ),
    },
  ];

  const judgeTypes = [
    { title: 'Article III Judges', count: '870', description: 'Appointed by the President and confirmed by the Senate. They serve during good behavior — effectively a lifetime appointment.' },
    { title: 'Bankruptcy Judges', count: '352', description: 'Appointed to 14-year terms by the court of appeals for the circuit in which the district is located.' },
    { title: 'Magistrate Judges', count: '564', description: 'Appointed by district court judges to 8-year terms. They handle a wide variety of judicial work.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner */}
      <div
        className="relative py-20 px-8"
        style={{
          background: 'linear-gradient(135deg, #20476f 0%, #0057a3 60%, #1a3a5c 100%)',
        }}
      >
        <div className="max-w-[65rem] mx-auto relative z-10">
          {/* Breadcrumb */}
          <p className="text-blue-200 text-sm mb-4">
            Home &rsaquo; About Federal Courts
          </p>
          <h1
            className="text-5xl font-light text-white mb-6 leading-tight"
            style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
          >
            About the Federal Courts
          </h1>
          <p className="text-blue-100 text-lg max-w-2xl leading-relaxed">
            The federal court system has three main levels: district courts, circuit courts of
            appeals, and the Supreme Court. Together they form the Judicial Branch of the
            United States Government.
          </p>
        </div>

        {/* Decorative lines */}
        <div className="absolute right-0 top-0 h-full w-64 opacity-10 hidden md:block">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute border-l border-white h-full"
              style={{ left: `${i * 36}px` }}
            />
          ))}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-16 z-40">
        <div className="max-w-[65rem] mx-auto px-8">
          <div className="flex gap-0 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? 'border-[#0057a3] text-[#0057a3] bg-white'
                    : 'border-transparent text-gray-600 hover:text-[#0057a3] hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-[65rem] mx-auto px-8 py-16">

        {/* Court Structure Tab */}
        {activeTab === 'structure' && (
          <div>
            <div className="flex flex-col md:flex-row gap-12 mb-16">
              <div className="flex-1">
                <h2
                  className="text-3xl font-semibold text-gray-900 mb-6"
                  style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
                >
                  Court Role and Structure
                </h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Article III of the Constitution invests the judicial power of the United States
                  in the federal court system. Article III, Section 1 specifically creates the U.S.
                  Supreme Court and gives Congress the authority to create the lower federal courts.
                </p>
                <p className="text-gray-600 leading-relaxed mb-6">
                  Congress has used this power to establish the 13 U.S. Courts of Appeals, the
                  94 U.S. District Courts, the U.S. Court of Claims, and the U.S. Court of
                  International Trade. U.S. Bankruptcy Courts handle bankruptcy cases.
                </p>
                <a
                  href="#"
                  className="inline-flex items-center text-[#0057a3] font-medium hover:underline"
                >
                  Learn more about court jurisdiction
                  <ArrowRightIcon className="w-4 h-4 ml-2" />
                </a>
              </div>

              {/* Pyramid diagram */}
              <div className="flex-shrink-0 w-full md:w-72">
                <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 text-center">
                  <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">
                    Court Hierarchy
                  </h3>
                  <div className="space-y-2">
                    {[
                      { label: 'Supreme Court', width: 'w-full', bg: 'bg-[#20476f]' },
                      { label: 'Courts of Appeals', width: 'w-4/5', bg: 'bg-[#0057a3]' },
                      { label: 'District Courts', width: 'w-3/5', bg: 'bg-[#4d8dc9]' },
                      { label: 'Bankruptcy Courts', width: 'w-2/5', bg: 'bg-[#89b4dd]' },
                    ].map(({ label, width, bg }) => (
                      <div key={label} className="flex justify-center">
                        <div
                          className={`${width} ${bg} text-white text-xs font-medium py-2.5 rounded-lg`}
                        >
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Court type cards */}
            <h2
              className="text-2xl font-semibold text-gray-900 mb-8"
              style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
            >
              Types of Federal Courts
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {courtTypes.map((court) => (
                <div
                  key={court.title}
                  className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-[#0057a3] hover:shadow-md transition-all group cursor-pointer"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center text-[#0057a3] flex-shrink-0 group-hover:bg-[#0057a3] group-hover:text-white transition-colors">
                      {court.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-lg mb-2">{court.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{court.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Federal Judges Tab */}
        {activeTab === 'judges' && (
          <div>
            <h2
              className="text-3xl font-semibold text-gray-900 mb-6"
              style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
            >
              About Federal Judges
            </h2>
            <p className="text-gray-600 leading-relaxed mb-12 max-w-3xl">
              Federal judges are appointed through a rigorous process and serve to ensure
              justice is administered impartially. The federal judiciary includes several
              categories of judges, each with distinct appointment processes and tenure.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {judgeTypes.map((type) => (
                <div key={type.title} className="bg-white border border-gray-200 rounded-2xl p-6 text-center hover:shadow-md transition-all">
                  <div className="text-4xl font-bold text-[#0057a3] mb-2">{type.count}</div>
                  <h3 className="font-semibold text-gray-900 mb-3">{type.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{type.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4" style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}>
                Judicial Vacancies
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Current judicial vacancies, nominees, and recent confirmations are tracked
                and published regularly to ensure transparency in the appointment process.
              </p>
              <button className="inline-flex items-center px-6 py-3 bg-[#0057a3] hover:bg-blue-700 text-white font-medium rounded-full transition-colors">
                View Current Vacancies
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* Jury Service Tab */}
        {activeTab === 'jury' && (
          <div>
            <h2
              className="text-3xl font-semibold text-gray-900 mb-6"
              style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
            >
              Jury Service
            </h2>
            <p className="text-gray-600 leading-relaxed mb-10 max-w-3xl">
              Jury service is one of the most important civic duties that citizens are called
              upon to perform. The right to a trial by jury is guaranteed by the Sixth and
              Seventh Amendments to the Constitution.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {[
                { title: 'Grand Jury', desc: 'A grand jury decides whether there is sufficient evidence to bring criminal charges against a defendant. Grand juries consist of 16–23 citizens.', step: '01' },
                { title: 'Petit Jury (Trial Jury)', desc: 'A trial jury, also known as a petit jury, decides whether a defendant in a civil or criminal trial is guilty or liable. They consist of 6 or 12 jurors.', step: '02' },
                { title: 'Juror Qualification', desc: 'To serve as a juror, you must be a U.S. citizen, at least 18 years old, able to speak and understand English, and not have any disqualifying factors.', step: '03' },
                { title: 'Juror Compensation', desc: 'Federal jurors are paid $50 per day. Jurors serving more than 10 days are paid $60 per day beginning on the 11th day of service.', step: '04' },
              ].map((item) => (
                <div key={item.title} className="flex gap-5">
                  <div className="text-4xl font-bold text-gray-100 flex-shrink-0 w-12 leading-none">{item.step}</div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-[#20476f] rounded-2xl p-8 text-white">
              <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}>
                Received a Jury Summons?
              </h3>
              <p className="text-blue-200 mb-6 leading-relaxed">
                If you have received a summons for jury duty, you can report to the court, request a postponement, or seek an exemption through the juror portal.
              </p>
              <button className="inline-flex items-center px-6 py-3 bg-[#efd388] hover:bg-yellow-300 text-gray-900 font-medium rounded-full transition-colors">
                Access Juror Portal
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* Defender Services Tab */}
        {activeTab === 'defender' && (
          <div>
            <h2
              className="text-3xl font-semibold text-gray-900 mb-6"
              style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}
            >
              Defender Services
            </h2>
            <p className="text-gray-600 leading-relaxed mb-10 max-w-3xl">
              The Sixth Amendment guarantees the right to counsel. Federal defender organizations
              provide legal representation to individuals who cannot afford to hire an attorney
              in federal criminal cases.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {[
                { title: 'Federal Public Defender Organizations', desc: 'Established in districts where the courts determine the volume of work requires one or more full-time, salaried attorneys. Employees of the Judicial Branch.' },
                { title: 'Community Defender Organizations', desc: 'Nonprofit defense counsel organizations that receive grants from the Judicial Conference and are established under the Criminal Justice Act.' },
                { title: 'Panel Attorneys', desc: 'Private attorneys appointed by the courts to represent CJA-eligible defendants when a conflict exists or when the defender organizations are not available.' },
                { title: 'CJA Eligibility', desc: 'Defendants financially unable to obtain adequate representation are entitled to have representation appointed under the Criminal Justice Act (CJA).' },
              ].map((item) => (
                <div key={item.title} className="bg-gray-50 border border-gray-200 rounded-2xl p-6 hover:border-[#0057a3] transition-colors">
                  <h3 className="font-semibold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 pt-8">
              <a href="#" className="inline-flex items-center text-[#0057a3] font-medium hover:underline">
                Find a Federal Defender Organization near you
                <ArrowRightIcon className="w-4 h-4 ml-2" />
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="bg-gray-50 border-t border-gray-200 py-12">
        <div className="max-w-[65rem] mx-auto px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-1" style={{ fontFamily: 'Source Serif Pro, Georgia, serif' }}>
                Can't find what you're looking for?
              </h3>
              <p className="text-gray-600 text-sm">Search our full library of federal court resources.</p>
            </div>
            <button className="inline-flex items-center px-8 py-3 bg-[#0057a3] hover:bg-blue-700 text-white font-medium rounded-full transition-colors flex-shrink-0">
              Search All Resources
              <ArrowRightIcon className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutFederalCourts;